<script setup lang="ts">
import tippy, {
  PopperElement,
  Props,
  roundArrow,
  animateFill as animateFillPlugin,
  Instance,
} from "tippy.js";
import { withDefaults, ref, onMounted, inject, watch } from "vue";

export type ProvideTippy = (el: PopperElement) => void;

interface TippyProps {
  refKey?: string;
  content: string;
  as?: string | object;
  options?: Partial<Props>;
  disabled?: boolean;
}

const props = withDefaults(defineProps<TippyProps>(), {
  as: "span",
  disabled: false,
});

const tippyRef = ref<PopperElement>();
const tippyInstance = ref<Instance<Props> | null>(null);

const init = (el: PopperElement, props: TippyProps) => {
    if (!props.disabled) {
        const instances = tippy(el, {
            plugins: [animateFillPlugin],
            content: props.content,
            arrow: roundArrow,
            popperOptions: {
            modifiers: [
                {
                name: "preventOverflow",
                options: {
                    rootBoundary: "viewport",
                },
                },
            ],
            },
            animateFill: false,
            animation: "shift-away",
            ...props.options,
        });
        tippyInstance.value = Array.isArray(instances) ? instances[0] : instances;
    }
    return
};

const bindInstance = (el: PopperElement) => {
  if (props.refKey) {
    const bind = inject<ProvideTippy>(`bind[${props.refKey}]`, () => {});
    if (bind) {
      bind(el);
    }
  }
};

const vTippyDirective = {
  mounted(el: PopperElement) {
    tippyRef.value = el;
  },
};

onMounted(() => {
  if (tippyRef.value) {
    init(tippyRef.value, props);
    bindInstance(tippyRef.value);
  }
});

// Watch for changes to content prop and update tippy content
watch(() => props.content, (newContent) => {
  if (tippyInstance.value && !props.disabled) {
    tippyInstance.value.setContent(newContent);
  }
});

// Watch for changes to disabled prop
watch(() => props.disabled, (isDisabled) => {
  if (tippyRef.value) {
    if (isDisabled && tippyInstance.value) {
      tippyInstance.value.destroy();
      tippyInstance.value = null;
    } else if (!isDisabled && !tippyInstance.value) {
      init(tippyRef.value, props);
    }
  }
});
</script>

<template>
  <component :is="as" v-tippy-directive class="cursor-pointer">
    <slot></slot>
  </component>
</template>
